import DownloadIcon from '@mui/icons-material/Download';
import FolderIcon from '@mui/icons-material/Folder';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Stack,
  type SvgIconProps,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import { mobileWidth } from '@/constants';
import { useLocalization } from '@/hooks/useLocalization';
import { FileType, getFileType } from './documentVaultGridUtils';

interface DocumentFileIconProps extends SvgIconProps {
  extension?: string | null;
  isFolder?: boolean;
}

export const DocumentFileIcon = ({
  extension,
  isFolder = false,
  ...iconProps
}: DocumentFileIconProps) => {
  if (isFolder) return <FolderIcon {...iconProps} />;

  const fileType = getFileType(extension);
  if (fileType === FileType.PDF) {
    return <PictureAsPdfOutlinedIcon {...iconProps} />;
  }
  if (fileType === FileType.Image) {
    return <ImageOutlinedIcon {...iconProps} />;
  }
  return <InsertDriveFileIcon {...iconProps} />;
};

interface DocumentSelectionBarProps {
  selectedCount: number;
  downloadCount: number;
  isDownloading: boolean;
  onDownload: () => void;
  leading?: ReactNode;
  children?: ReactNode;
}

export const DocumentSelectionBar = ({
  selectedCount,
  downloadCount,
  isDownloading,
  onDownload,
  leading,
  children,
}: DocumentSelectionBarProps) => {
  const { t } = useLocalization();
  const isXs = useMediaQuery(mobileWidth);

  return (
    <Box
      sx={{
        mb: 1.5,
        p: 1.5,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        display: 'flex',
        alignItems: isXs ? 'stretch' : 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        flexDirection: isXs ? 'column' : 'row',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 0,
        }}
      >
        {leading}
        <Typography
          variant='body2'
          sx={{ fontWeight: 600 }}
        >
          {t('documentVault.selectedCount', {
            defaultValue: '{{count}} selected',
            count: selectedCount,
          })}
        </Typography>
      </Box>

      <Stack
        direction={isXs ? 'column' : 'row'}
        spacing={1}
        flexWrap={isXs ? 'nowrap' : 'wrap'}
        useFlexGap
      >
        <Tooltip
          title={
            downloadCount === 0
              ? t(
                  'documentVault.bulkDownloadSelectionHint',
                  'Select at least one downloadable item to use Download.',
                )
              : ''
          }
          disableHoverListener={downloadCount > 0}
        >
          <span>
            <Button
              fullWidth={isXs}
              variant='outlined'
              startIcon={
                isDownloading ? (
                  <CircularProgress
                    size={16}
                    color='inherit'
                  />
                ) : (
                  <DownloadIcon />
                )
              }
              onClick={onDownload}
              disabled={isDownloading || downloadCount === 0}
            >
              {t('download', 'Download')} ({downloadCount})
            </Button>
          </span>
        </Tooltip>
        {children}
      </Stack>
    </Box>
  );
};

export type DocumentActionKind = 'preview' | 'download';

interface DocumentActionErrorSnackbarProps {
  failedAction: DocumentActionKind | null;
  onRetry: () => void;
  onClose: () => void;
}

export const DocumentActionErrorSnackbar = ({
  failedAction,
  onRetry,
  onClose,
}: DocumentActionErrorSnackbarProps) => {
  const { t } = useLocalization();
  // The message keeps the last kind while the snackbar closes.
  const [shownAction, setShownAction] = useState(failedAction);
  if (failedAction !== null && failedAction !== shownAction) {
    setShownAction(failedAction);
  }

  return (
    <Snackbar
      open={failedAction !== null}
      autoHideDuration={6000}
      onClose={(_, reason) => {
        if (reason !== 'clickaway') onClose();
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity='error'
        variant='filled'
        onClose={onClose}
        action={
          <Button
            color='inherit'
            size='small'
            onClick={onRetry}
          >
            {t('retry', 'Retry')}
          </Button>
        }
        sx={{ width: '100%', alignItems: 'center' }}
      >
        {(failedAction ?? shownAction) === 'preview'
          ? t('documentVault.previewFailed', 'Preview was unable to be loaded')
          : t(
              'documentVault.downloadFailed',
              'Document was unable to be downloaded',
            )}
      </Alert>
    </Snackbar>
  );
};
